import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useContext,
  useRef,
} from 'react';
import classNames from 'classnames';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { Button, Modal } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import useCallbackRef from 'hooks/useCallbackRef';
import InputField from 'components/InputField';
import validations from 'validations';
import TextAreaField from 'components/TextAreaField';
import api from 'api';
import ListingContext from '../../../../providers/Listing/Listing.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { sentryError } from 'utils';
import HandIcon from 'icons/Hand.icon';
import SelectField from 'components/SelectField';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useUserBasicInfo from 'hooks/useUserBasicInfo';
import ContactNumberField from 'components/ContactNumberField';
import EmailField from 'components/EmailField';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import {
  ENQUIRY_ORIGIN,
  EnquiryCreationInitiatedEventProps,
} from 'models/MixpanelEvents';

import './MakeNewLeadModal.styles.scss';

type MakeNewLeadModalProps = {
  className?: string;
  isPrivate: boolean;
  onInvitePrivate: () => void;
};

const MakeNewLeadModal: React.ForwardRefRenderFunction<
  OverlayRef,
  MakeNewLeadModalProps
> = (props, ref) => {
  const { className, isPrivate, onInvitePrivate } = props;

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);
  const { isSuperAdmin } = useContext(CurrentUserContext);

  const {
    listing: { _id: listingId },
    dealRoom: { _id: dealId, name: dealName, bossModeReferrerOptions },
    setEnquiry,
    refetchLeads,
  } = useContext(ListingContext);

  const formRef = useRef(null);

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const classes = classNames('make-new-lead-modal', className);

  const {
    isFetchingUserBasicInfo,
    isUserBasicInfoSet,
    onEmailChange,
    resetState,
  } = useUserBasicInfo();

  const handleOnSubmit = useCallback(
    async (values) => {
      try {
        mixpanelService.enquiryCreationInitiated(
          getMixpanelProps({
            dealId,
            dealName,
            enquiryOrigin: ENQUIRY_ORIGIN.MANUAL,
          }) as EnquiryCreationInitiatedEventProps,
        );

        const {
          data: { listingEnquiry },
        } = await api.dealRoom.inviteLead(listingId, values);
        setEnquiry(listingEnquiry);
        refetchLeads();
        popSuccess(t('successfullyCreatedLead'));
        modal?.close();
      } catch (e) {
        sentryError(e);
        popServerError(e);
      }
    },
    [
      listingId,
      modal,
      dealName,
      dealId,
      getMixpanelProps,
      popServerError,
      popSuccess,
      refetchLeads,
      setEnquiry,
      t,
    ],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('inviteAContactOfYours')}
      onClose={resetState}
    >
      <Form
        onSubmit={handleOnSubmit}
        render={({ handleSubmit, submitting, valid, form, values }) => {
          formRef.current = values;

          return (
            <form onSubmit={handleSubmit} className="make-new-lead-modal__form">
              <div className="banner">
                <p className="title">
                  <HandIcon />
                  {t('makeNewLeadModalBannerTitle')}
                </p>
                {isPrivate && (
                  <p className="description">
                    {t('makeNewLeadModalBannerDescription')}
                    <span onClick={onInvitePrivate}>
                      {t('inviteThemInstead.')}
                    </span>
                  </p>
                )}
              </div>
              <p>{t('inviteAContactOfYoursMessage')}</p>
              <div className="make-new-lead-modal__form__row">
                <Field
                  name="firstName"
                  label={t('firstName')}
                  placeholder={t('firstName')}
                  component={InputField}
                  validate={validations.composeValidators(
                    validations.required(),
                    validations.noLongerThan(
                      50,
                      t('noLongerThan', { length: 50 }),
                    ),
                    validations.fullNameValidation(
                      t('specialCharactersOrNumbersNotAllowed'),
                    ),
                  )}
                  disabled={isUserBasicInfoSet}
                  required
                />
                <Field
                  name="lastName"
                  label={t('lastName')}
                  placeholder={t('lastName')}
                  component={InputField}
                  required
                  validate={validations.composeValidators(
                    validations.required(),
                    validations.noLongerThan(
                      50,
                      t('noLongerThan', { length: 50 }),
                    ),
                    validations.fullNameValidation(
                      t('specialCharactersOrNumbersNotAllowed'),
                    ),
                  )}
                  disabled={isUserBasicInfoSet}
                />
              </div>
              <div className="make-new-lead-modal__form__row">
                <EmailField
                  required
                  onChangeHandle={(email) => onEmailChange({ email, form })}
                />
                <ContactNumberField />
              </div>

              <Field
                label={t('petsonalMessageToThisPerson')}
                name="notesAboutEnquiry"
                description={t('thisOneIsOptional')}
                component={TextAreaField}
              />
              {isSuperAdmin && (
                <Field
                  label={t('whoAddedThisLead')}
                  component={SelectField}
                  options={bossModeReferrerOptions.map((opt) => ({
                    label: opt,
                    value: opt,
                  }))}
                  name="referrerName"
                />
              )}
              <Button
                type="submit"
                disabled={submitting || !valid || isFetchingUserBasicInfo}
              >
                {t('addNewEnquiry')}
              </Button>
            </form>
          );
        }}
      />
    </Modal>
  );
};

export default forwardRef(MakeNewLeadModal);
